// Reusable inline edit functionality for database tables
// Supports: clients, suppliers, materials, hire_items, flowers

// Store original values when entering edit mode
const originalValues = {}

/**
 * Toggle edit mode for a table row
 * @param {string|number} id - The resource ID
 * @param {Object} config - Configuration object
 * @param {string} config.resourceType - Resource type (client, supplier, material, hire_item, flower)
 * @param {string} [config.rowId] - Explicit id of the element scoping this
 *   resource's fields. Needed when the same resource is on the page more than
 *   once (e.g. the clients index renders a desktop table row *and* a mobile
 *   accordion card) — only one of them can own the derived `<type>-row-<id>`,
 *   so the other passes its own id and edits its own copy of the fields.
 * @param {boolean} isCancel - Whether this is a cancel action
 */
window.toggleEditMode = function (id, config = {}, isCancel = false) {
  // Support legacy call signature: toggleEditMode(id) for backward compatibility
  if (typeof config === 'boolean') {
    isCancel = config
    config = {}
  }

  const resourceType = config.resourceType || inferResourceType()
  // Convert resource type to row ID format (e.g., 'hire_item' -> 'hire-item')
  const rowIdPrefix = getRowIdPrefix(resourceType)
  const rowId = config.rowId || `${rowIdPrefix}-row-${id}`
  const row = document.getElementById(rowId)

  if (!row) {
    console.error(`Row not found: ${rowId}`)
    return
  }

  const displayModes = row.querySelectorAll('.display-mode')
  const editModes = row.querySelectorAll('.edit-mode')
  const editBtn = row.querySelector('.edit-toggle-btn')
  const editText = row.querySelector('.edit-text')
  const saveText = row.querySelector('.save-text')

  // Check if we're in edit mode by checking if the first edit mode cell is visible
  const isEditMode = editModes[0] && editModes[0].style.display !== 'none'

  if (isEditMode) {
    if (isCancel) {
      // Cancel mode - revert to original values and exit edit mode
      const inputs = row.querySelectorAll(
        '.edit-mode input, .edit-mode textarea, .edit-mode select'
      )
      const original = originalValues[`${resourceType}-${id}`]

      if (original) {
        inputs.forEach((input) => {
          const field = input.dataset.field
          if (field && original[field] !== undefined) {
            input.value = original[field]
          }
        })
      }

      // Exit edit mode
      displayModes.forEach((mode) => (mode.style.display = ''))
      editModes.forEach((mode) => (mode.style.display = 'none'))
      delete originalValues[`${resourceType}-${id}`]

      // Hide save/cancel buttons, show dropdown
      const actionsDisplay = row.querySelector('.actions-display-mode')
      const actionsEdit = row.querySelector('.actions-edit-mode')
      if (actionsDisplay) actionsDisplay.style.display = ''
      if (actionsEdit) actionsEdit.style.display = 'none'
    } else {
      // Save mode - submit changes
      saveResource(id, row, resourceType, config)
    }
  } else {
    // Enter edit mode - show edit fields and store original values
    const inputs = row.querySelectorAll(
      '.edit-mode input, .edit-mode textarea, .edit-mode select'
    )
    const original = {}

    inputs.forEach((input) => {
      const field = input.dataset.field
      if (field) {
        original[field] = input.value
      }
    })

    originalValues[`${resourceType}-${id}`] = original

    displayModes.forEach((mode) => (mode.style.display = 'none'))
    editModes.forEach((mode) => (mode.style.display = ''))

    // Update button text if using old pattern
    if (editText) editText.style.display = 'none'
    if (saveText) saveText.style.display = ''
    if (editBtn) editBtn.title = 'Save changes'

    // Close any open dropdowns
    document.querySelectorAll('.dropdown-menu').forEach((menu) => {
      menu.classList.add('hidden')
    })

    // Show save/cancel buttons, hide dropdown
    const actionsDisplay = row.querySelector('.actions-display-mode')
    const actionsEdit = row.querySelector('.actions-edit-mode')
    if (actionsDisplay) actionsDisplay.style.display = 'none'
    if (actionsEdit) actionsEdit.style.display = ''
  }
}

/**
 * Cancel edit mode
 * @param {string|number} id - The resource ID
 * @param {Object} config - Configuration object
 */
window.cancelEditMode = function (id, config = {}) {
  toggleEditMode(id, config, true)
}

/**
 * Save resource changes
 * @param {string|number} id - The resource ID
 * @param {HTMLElement} row - The table row element
 * @param {string} resourceType - Resource type
 * @param {Object} config - Configuration object
 */
function saveResource(id, row, resourceType, config = {}) {
  const formData = new FormData()
  const inputs = row.querySelectorAll(
    '.edit-mode input, .edit-mode textarea, .edit-mode select'
  )

  // Determine form data prefix
  const formPrefix = config.formPrefix || getFormPrefix(resourceType)

  inputs.forEach((input) => {
    if (input.dataset.field && !input.readOnly) {
      formData.append(`${formPrefix}[${input.dataset.field}]`, input.value)
    }
  })

  // Add CSRF token
  const csrfToken = document.querySelector('meta[name="csrf-token"]')
  if (csrfToken) {
    formData.append('authenticity_token', csrfToken.getAttribute('content'))
  }

  // Determine API endpoint
  const endpoint = config.endpoint || `/${getResourcePath(resourceType)}/${id}`
  const successMessage =
    config.successMessage || `${capitalize(resourceType)} updated successfully!`

  // Submit via fetch
  fetch(endpoint, {
    method: 'PATCH',
    body: formData,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json',
    },
  })
    .then((response) => {
      if (response.ok) {
        return response.json()
      } else {
        return response.json().then((data) => {
          throw new Error(data.error || 'Save failed')
        })
      }
    })
    .then((data) => {
      if (data.success) {
        if (typeof window.showSuccess === 'function') {
          window.showSuccess(successMessage)
        }
        setTimeout(() => window.location.reload(), 1000)
      } else {
        const errorMsg =
          data.error || `Error saving ${resourceType}. Please try again.`
        if (typeof window.showError === 'function') {
          window.showError(errorMsg)
        } else {
          alert(errorMsg)
        }
      }
    })
    .catch((error) => {
      const errorMsg =
        error.message || `Error saving ${resourceType}. Please try again.`
      if (typeof window.showError === 'function') {
        window.showError(errorMsg)
      } else {
        alert(errorMsg)
      }
      console.error('Save error:', error)
    })
}

/**
 * Infer resource type from context
 */
function inferResourceType() {
  // Try to infer from URL
  const path = window.location.pathname
  if (path.includes('/clients')) return 'client'
  if (path.includes('/suppliers')) return 'supplier'
  if (path.includes('/materials')) return 'material'
  if (path.includes('/hire_items')) return 'hire_item'
  if (path.includes('/flowers')) return 'flower'

  // Default fallback
  return 'client'
}

/**
 * Get form data prefix for resource type
 */
function getFormPrefix(resourceType) {
  const prefixes = {
    client: 'client',
    supplier: 'supplier',
    material: 'additional_material',
    hire_item: 'hire_item',
    flower: 'flower',
  }
  return prefixes[resourceType] || resourceType
}

/**
 * Get resource path for API endpoint
 */
function getResourcePath(resourceType) {
  const paths = {
    client: 'clients',
    supplier: 'suppliers',
    material: 'materials',
    hire_item: 'hire_items',
    flower: 'flowers',
  }
  return paths[resourceType] || resourceType
}

/**
 * Capitalize first letter
 */
function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Get row ID prefix for resource type (converts underscores to hyphens)
 */
function getRowIdPrefix(resourceType) {
  // Convert underscores to hyphens for HTML IDs
  return resourceType.replace(/_/g, '-')
}
